import { MutationCache, QueryCache, QueryClient } from "@tanstack/react-query";

import { ApiError, HTTP, isTransient } from "./api-error.ts";
import { analytics } from "./telemetry/analytics.ts";

export { errorMessage } from "./api-error.ts";

/**
 * A quién se avisa de CADA fallo de una query o una mutación, además de a la
 * pantalla que la lanzó.
 *
 * La pantalla enseña el mensaje donde toca; esto es lo que no es de ninguna
 * pantalla: que la sesión caducó, o dejar rastro de la referencia de un 500.
 */
export type QueryErrorHandler = (error: unknown) => void;

/**
 * Lo que se hace con todo fallo, sea de quien sea: dejar en la consola la
 * referencia de un error del servidor. La pantalla ya la enseña; aquí queda
 * junto a la petición que lo produjo, que es lo que se mira al depurar.
 */
export const reportServerError: QueryErrorHandler = (error) => {
  if (error instanceof ApiError && error.status >= HTTP.internal) {
    console.error(
      `[api] ${String(error.status)} ${error.code}`,
      error.errorId ?? "(sin referencia)",
      error.message,
    );
  }
};

/**
 * Deja constancia de un fallo del API en la telemetría: estado, código, el
 * mensaje y el primer tramo de la clave (`conversations`, `participants`…).
 */
const trackApiError = (
  error: unknown,
  source: "query" | "mutation",
  key: readonly unknown[] | undefined,
): void => {
  if (!(error instanceof ApiError)) return;
  const first = key?.[0];
  analytics.app.apiError({
    status: error.status,
    code: error.code,
    source,
    reason: error.message,
    ...(typeof first === "string" ? { key: first } : {}),
  });
};

/**
 * El cliente de TanStack Query: una sola caché para toda la aplicación.
 *
 * Sustituye a los `useEffect` + `useState` que cada panel tenía repetidos, y
 * al `Map` que el cargador de datasets llevaba a mano. Lo que aporta no es
 * ahorrar líneas: es que estado de carga, error, caché, reintentos y
 * deduplicación de peticiones simultáneas dejen de implementarse cinco veces
 * de cinco maneras distintas.
 */
export const createQueryClient = (
  onError: QueryErrorHandler = reportServerError,
): QueryClient =>
  new QueryClient({
    queryCache: new QueryCache({
      onError: (error, query) => {
        trackApiError(error, "query", query.queryKey);
        onError(error);
      },
    }),
    mutationCache: new MutationCache({
      onError: (error, _variables, _context, mutation) => {
        trackApiError(error, "mutation", mutation.options.mutationKey);
        onError(error);
      },
    }),
    defaultOptions: {
      queries: {
        /**
         * Los datos se consideran frescos un minuto.
         *
         * Casi todo lo que se lee aquí --el padrón, las mecánicas, un
         * dataset-- cambia porque alguien lo cambia, no solo por pasar el
         * tiempo. Un minuto evita repedir lo mismo al ir y volver entre
         * pantallas sin llegar a enseñar algo viejo.
         */
        staleTime: 60_000,
        /**
         * No se reintenta lo que el servidor ya respondió.
         *
         * Un 404 o un 422 no mejoran por insistir: son la respuesta. Solo se
         * reintenta lo que pinta a problema de red o a un servidor de paso
         * caído, y una vez.
         */
        retry: (failureCount, error) => isTransient(error) && failureCount < 1,
        refetchOnWindowFocus: false,
      },
      mutations: {
        retry: false,
      },
    },
  });
